import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, Loader2, AlertCircle, Share2, Copy, Lock } from "lucide-react";
import { toast } from "sonner";
import LockedAction from "@/components/LockedAction";

/**
 * VoiceNoteTimelineExtras. inline audio player + "Transcribe now" button
 * for a voice_note Document on the timeline. Mounts inside the existing
 * timeline card. Hidden when the doc isn't a voice note.
 */
export default function VoiceNoteTimelineExtras({ doc, onUpdated }) {
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [shareEnabled, setShareEnabled] = useState(Boolean(doc?.share_enabled && doc?.share_token));
  const [sharing, setSharing] = useState(false);

  // Free the blob URL when the row unmounts so we don't leak memory if the
  // user expands lots of voice notes on a long-running case page.
  useEffect(() => () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  }, [audioUrl]);

  if (!doc || doc.doc_type !== "voice_note") return null;

  const isFree = !user || user.tier === "free";
  // For paid users: daily counter. For free: lifetime taste (2 transcriptions).
  const freeLifetime = user?.voice_transcribes_lifetime || 0;
  const freeLifetimeCap = user?.free_voice_transcribes_lifetime_cap || 2;
  const remainingDaily = Math.max(
    0,
    (user?.voice_transcribes_daily_limit || 0) - (user?.voice_transcribes_used_today || 0),
  );
  const remainingLifetime = Math.max(0, freeLifetimeCap - freeLifetime);
  const remaining = isFree ? remainingLifetime : remainingDaily;
  const canTranscribe = remaining > 0;
  const transcribed = doc.transcript_status === "completed" && doc.extracted_text;
  const failed = doc.transcript_status === "failed";

  const loadAudio = async () => {
    if (audioUrl || loadingAudio) return;
    setLoadingAudio(true);
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      setAudioUrl(URL.createObjectURL(res.data));
    } catch (e) {
      toast.error("Couldn't load audio. try again in a moment.");
    } finally {
      setLoadingAudio(false);
    }
  };

  const transcribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/voice-notes/${doc.id}/transcribe`);
      onUpdated?.(data);
      await refreshUser();
      toast.success("Voice note transcribed.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transcription failed.");
    } finally {
      setBusy(false);
    }
  };

  const enableShare = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/voice-notes/${doc.id}/share`);
      const url = data.public_url || data.share_url;
      // Build an absolute URL if the backend returned a relative one.
      const absolute = url.startsWith("http") ? url : `${window.location.origin}${url}`;
      setShareUrl(absolute);
      setShareEnabled(true);
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(absolute).catch(() => {});
        toast.success("Share link copied to clipboard.");
      } else {
        toast.success("Share link ready.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create share link.");
    } finally {
      setSharing(false);
    }
  };

  const revokeShare = async () => {
    setSharing(true);
    try {
      await api.delete(`/voice-notes/${doc.id}/share`);
      setShareEnabled(false);
      setShareUrl(null);
      toast.success("Share link revoked.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed.");
    } finally {
      setSharing(false);
    }
  };

  const copyExistingLink = async () => {
    if (!doc?.share_token) return;
    const absolute = `${window.location.origin}/v/${doc.share_token}`;
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(absolute).catch(() => {});
      toast.success("Share link copied.");
    }
    setShareUrl(absolute);
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#EFEDE5] space-y-2" data-testid={`voice-extras-${doc.id}`}>
      {audioUrl ? (
        <audio controls src={audioUrl} className="w-full" data-testid={`voice-audio-${doc.id}`} />
      ) : (
        <button
          onClick={loadAudio}
          disabled={loadingAudio}
          className="btn-ghost text-xs inline-flex items-center gap-1"
          data-testid={`voice-load-audio-${doc.id}`}
        >
          {loadingAudio ? <Loader2 size={12} className="animate-spin" /> : "▶"} Play audio
          {doc.duration_seconds ? ` (${Math.floor(doc.duration_seconds / 60)}:${String(doc.duration_seconds % 60).padStart(2, "0")})` : ""}
        </button>
      )}

      {!transcribed && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          {(isFree && !canTranscribe) ? (
            <LockedAction locked={true} reason="transcribe" tier="copilot" label="Transcribe" inline testid={`voice-transcribe-locked-${doc.id}`}>
              <span className="btn-secondary text-xs inline-flex items-center gap-1.5 pointer-events-none opacity-90">
                <Sparkles size={12} strokeWidth={1.5} /> Transcribe now
              </span>
            </LockedAction>
          ) : (
            <button
              onClick={transcribe}
              disabled={busy || !canTranscribe}
              className="btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid={`voice-transcribe-${doc.id}`}
              title={canTranscribe ? "" : "Transcription limit reached"}
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} strokeWidth={1.5} />}
              {failed ? "Retry transcription" : "Transcribe now"}
            </button>
          )}
          <span className="text-[10px] font-mono text-[#6B7280]" data-testid={`voice-transcribe-counter-${doc.id}`}>
            {isFree
              ? `${remainingLifetime}/${freeLifetimeCap} free transcriptions left`
              : `${remaining}/${user?.voice_transcribes_daily_limit || 0} left today`}
          </span>
        </div>
      )}

      {failed && (
        <div className="text-[11px] text-[#B35D4C] inline-flex items-center gap-1">
          <AlertCircle size={11} strokeWidth={1.5} /> Last transcription attempt failed.
        </div>
      )}

      {/* Share controls — public read-only link the user can SMS to a friend */}
      <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
        {!shareEnabled ? (
          <button
            onClick={enableShare}
            disabled={sharing}
            className="btn-ghost text-xs inline-flex items-center gap-1.5"
            data-testid={`voice-share-${doc.id}`}
          >
            {sharing ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={12} strokeWidth={1.5} />}
            Share link
          </button>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={shareUrl ? () => { navigator.clipboard?.writeText(shareUrl); toast.success("Copied."); } : copyExistingLink}
              className="btn-ghost text-xs inline-flex items-center gap-1.5"
              data-testid={`voice-share-copy-${doc.id}`}
            >
              <Copy size={12} strokeWidth={1.5} /> Copy link
            </button>
            <button
              onClick={revokeShare}
              disabled={sharing}
              className="btn-ghost text-xs inline-flex items-center gap-1.5 text-[#B35D4C]"
              data-testid={`voice-share-revoke-${doc.id}`}
            >
              <Lock size={12} strokeWidth={1.5} /> Revoke
            </button>
          </div>
        )}
        {shareUrl && (
          <span className="text-[10px] font-mono text-[#6B7280] truncate max-w-[55%]" title={shareUrl}>
            {shareUrl.replace(/^https?:\/\//, "")}
          </span>
        )}
      </div>
    </div>
  );
}
